import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { WidgetAssetListComponent } from '../../../products/components/widget-asset-list/widget-asset-list.component';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { FormValidationStatus } from '../../../../@core/types/form-validation-status.type';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { Journal } from '../../../../generated/generated-types';
import { AssetFileInputComponent } from '../../../../shared/components/asset-file-input/asset-file-input.component';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { AssetService } from '../../../asset/services/asset.service';
import { WidgetAssetsManagerComponent } from '../../../asset/widget-assets-manager/widget-assets-manager.component';
import { JournalService } from '../../services/journal.service';

@Component({
    selector: 'app-widget-journal-assets-manager',
    templateUrl: './widget-journal-assets-manager.component.html',
    styleUrls: ['./widget-journal-assets-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        AssetFileInputComponent,
        WidgetAssetListComponent,
        BlockableDiv,
        ButtonModule,
        AssetFileInputComponent,
        TranslatePipe,
        TooltipModule,
    ]
})
export class WidgetJournalAssetsManagerComponent extends WidgetAssetsManagerComponent<Journal> {
    protected destroyRef = inject(DestroyRef);

    isSaving = signal<boolean>(false);

    // Reactive form validity tracking
    canSave = signal<boolean>(false);

    // Computed tooltip message based on form validity
    saveButtonTooltip = computed(() => {
        return this.canSave()
            ? this.i18n.translate('journal.missing-data-for-save')
            : this.i18n.translate('journal.save-journal-first');
    });

    // Track form subscription for cleanup
    private formSubscription?: () => void;

    get journal() {
        return this.model as Journal;
    }

    constructor(
        /** Needs to Implement IBaseModelAssetService */
        public journalService: JournalService,
        public assetService: AssetService,
        public notificationService: NotificationService,
        public i18n: I18nService
    ) {
        super(journalService, assetService, notificationService, i18n);
        this.destroyRef.onDestroy(() => {
            console.log(`Destroying ${this.constructor.name}`);
            // Clean up form subscription if it exists
            this.formSubscription?.();
        });

        // Subscribe to form validity changes
        this.subscribeToFormChanges();
    }

    private subscribeToFormChanges() {
        // Only subscribe if we don't have an ID yet
        if (this.journal?.id) {
            return;
        }

        // Use effect to watch for when the form becomes available
        effect(() => {
            const component = this.options.formState?.detailComponent as BaseDetailComponent<Journal>;
            if (component?.detailForm && !this.journal?.id) {
                // Clean up any existing subscription
                this.formSubscription?.();

                // Subscribe to form status changes
                const subscription = component.detailForm.statusChanges
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((status) => {
                        this.canSave.set(status === FormValidationStatus.VALID);
                    });

                // Set initial state
                this.canSave.set(component.detailForm.valid);

                // Store cleanup function
                this.formSubscription = () => subscription.unsubscribe();
            }
        });
    }

    saveToUnlockEditMode = async () => {
        this.isSaving.set(true);
        try {
            let component = this.options.formState.detailComponent as BaseDetailComponent<Journal>;
            await component.save(null, null, true);
            this.notificationService.success(_("common.save-successful"));

            // Once saved and we have an ID, we can unsubscribe from form changes
            if (this.journal?.id) {
                this.formSubscription?.();
                this.formSubscription = undefined;
            }
        } catch (error) {
            this.notificationService.error(_("common.save-error"));
        } finally {
            this.isSaving.set(false);
        }
    };
} 